import { DvToolbarAction } from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TabBase } from '@core/base/tab.base';
import { DialogService } from '@core/services/dialog.service';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity } from '@shared/constants/entity';
import { AssetsLogGridService } from './assets-log-grid.service';
import { FullScreenMapComponent } from '@features/map/full-screen-map/full-screen-map.component';
import {
  Coordinates,
  FullScreenMapProps,
  MapMarker,
  MapInput,
} from '@features/map/interfaces';
import DateUtils from '@shared/utils/date.utils';
import {
  ApplyColumnStateParams,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { filter, of } from 'rxjs';
import { MapId } from '@features/map/constants/map-id.enum';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-assets-log-tab',
  templateUrl: './assets-log-tab.component.html',
  providers: [AssetsLogGridService, GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AssetsLogTabComponent extends TabBase implements OnInit {
  private gridOptionsService = inject(GridOptionsService);
  private assetGridService = inject(AssetsLogGridService);
  private dialog = inject(DialogService);

  actions = signal<DvToolbarAction[]>([]);
  gridOptions: GridOptions;

  readonly customColumnState: ApplyColumnStateParams = {
    state: [
      {
        colId: 'UTCTime',
        sort: 'desc',
      },
    ],
  };

  private gridApi: GridApi | undefined;
  private destroyRef = inject(DestroyRef);

  constructor() {
    super(inject(ActivatedRoute).parent!);
  }

  ngOnInit(): void {
    this.gridOptions = {
      ...this.gridOptionsService.serverSide(
        this.assetGridService.colDefs,
        Entity.AssetLog
      ),
    };
    this.entityId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(entityId => !!entityId)
      )
      .subscribe(entityId => {
        if (entityId.includes('_')) return;
        this.gridOptions.context.extraFilter = this.getFilterModel(entityId);
        this.gridApi?.onFilterChanged();
      });

    this.actions.set(this.assetGridService.getGridActions(this.showOnMap));
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    const selectedNode = api.getSelectedNodes()[0];

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'map_marker_move') {
          return { ...action, disabled: !selectedNode };
        }
        return action;
      })
    );
  }

  showOnMap = (coordinates: Coordinates, lastMoved: string): void => {
    const marker: MapMarker = {
      ...coordinates,
      options: {
        customMarker: { iconUrl: './assets/viatracks-find.svg' },
        isToolTipPermanent: true,
        customTooltip: `<div><b>${DateUtils.convertDateTimeToNO(
          new Date(lastMoved)
        )}</b><br /><b>${coordinates.lat}  ${coordinates.lng}</b></div>`,
      },
    };
    const data: FullScreenMapProps = {
      id: MapId.AssetLogMap,
      markers$: of([{ points: [marker] }] satisfies MapInput[]),
    };

    this.dialog.open<FullScreenMapProps, FullScreenMapComponent>({
      size: 'full',
      contentComponent: FullScreenMapComponent,
      hideFooter: true,
      hideHeader: true,
      data,
    });
  };

  private getFilterModel(entityId: string): FilterModelWithKeys {
    return {
      AssetID: {
        filterType: 'number',
        type: 'equals',
        filter: entityId,
      },
    };
  }
}
